import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { differenceInDays } from 'date-fns';
import { setNPSOpen } from '../redux/actions/npsActions';

const NPS_COOLDOWN_DAYS = 15;

const EMOJI_RATINGS = {
  1: '😢',
  2: '😞',
  3: '😐',
  4: '😊',
  5: '😁',
};

const RATING_LABELS = {
  1: 'VERY_BAD',
  2: 'BAD',
  3: 'REGULAR',
  4: 'GOOD',
  5: 'EXCELLENT',
};

const RATING_COLORS = {
  1: '#e74c3c',
  2: '#ff8c00',
  3: '#f1c40f',
  4: '#2ecc71',
  5: '#27ae60',
};

const PLATFORM = 'PARTNERS';
const NPS_STORAGE_KEY = 'nps_cooldowns';

const getNPSCooldowns = () => {
  const stored = localStorage.getItem(NPS_STORAGE_KEY);
  return stored ? JSON.parse(stored) : [];
};

const setNPSCooldowns = (cooldowns) => {
  localStorage.setItem(NPS_STORAGE_KEY, JSON.stringify(cooldowns));
};

export const useNPSModal = () => {
  const dispatch = useDispatch();
  const { isNPSOpen, npsProcessType } = useSelector((state) => state.nps);

  const checkCooldown = useCallback((processType) => {
    if (!processType) return false;

    const cooldowns = getNPSCooldowns();
    const cooldownKey = `nps_${processType}`;
    const cooldownItem = cooldowns.find((item) => item[cooldownKey] !== undefined);

    if (cooldownItem) {
      const lastDate = new Date(cooldownItem[cooldownKey]);
      const now = new Date();
      const daysDiff = differenceInDays(now, lastDate);

      return daysDiff < NPS_COOLDOWN_DAYS;
    }

    return false;
  }, []);

  const closeNPSModal = useCallback(() => {
    dispatch(setNPSOpen(false));
  }, [dispatch]);

  const setCooldown = useCallback(() => {
    if (npsProcessType) {
      const cooldownKey = `nps_${npsProcessType}`;
      const cooldowns = getNPSCooldowns();

      const existingIndex = cooldowns.findIndex((item) => item[cooldownKey] !== undefined);
      const newCooldownItem = {
        [cooldownKey]: new Date().toISOString(),
      };

      if (existingIndex >= 0) {
        cooldowns[existingIndex] = newCooldownItem;
      } else {
        cooldowns.push(newCooldownItem);
      }

      setNPSCooldowns(cooldowns);
    }
  }, [npsProcessType]);

  useEffect(() => {
    const random = Math.random();
    if (npsProcessType && !checkCooldown(npsProcessType) && random > 0.5) {
      const timer = setTimeout(() => {
        dispatch(setNPSOpen(true));
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [npsProcessType, checkCooldown, dispatch]);

  return {
    isNPSOpen,
    npsProcessType,
    closeNPSModal,
    setCooldown,
    EMOJI_RATINGS,
    RATING_LABELS,
    RATING_COLORS,
    PLATFORM,
  };
};
